import React, { useEffect } from 'react';
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFormClearErrors,
  UseFormSetError
} from 'react-hook-form';
import UploadFilesField from '@/components/molecules/UploadFilesField';
import InputField from '@/components/atoms/InputField';
import SelectField from '@/components/atoms/SelectField';
import DatePickerField from '@/components/atoms/DatePickerField';
import TabRadioField from '@/components/atoms/TabRadioField';
import euroIcon from '@/assets/icons/euro.svg';
import PdfThumbnail from '@/components/molecules/PdfThumbnail';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const currencies = [
  { value: 'EUR', label: 'Euro' },
  { value: 'USD', label: 'US Dollar' }
];

interface CreateInvoiceUploadProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  uploadedFiles: File[];
  onFilesChange: (files: File[]) => void;
  budgetOptions: { value: string; label: string }[];
  existingImageUrls?: string[];
  onRemoveExistingImage?: (url: string) => void;
  maxAmount?: number;
  isAmountDisabled?: boolean;
  invoiceCategory?: string;
  paymentTermType?: string;
  setPaymentTermType?: (value: 'fixed' | 'window') => void;
}

const CreateInvoiceUpload: React.FC<CreateInvoiceUploadProps> = ({
  register,
  errors,
  watch,
  setValue,
  setError,
  clearErrors,
  uploadedFiles,
  onFilesChange,
  budgetOptions,
  existingImageUrls = [],
  onRemoveExistingImage,
  maxAmount,
  isAmountDisabled = false,
  invoiceCategory = 'expense',
  paymentTermType = 'fixed',
  setPaymentTermType = (value: 'fixed' | 'window') => {}
}) => {
  const paymentIssueDate = watch('payment_issue_date');
  const paymentDate = watch('payment_date');
  const paymentTermDate = watch('payment_term_date');
  const paymentAmount = watch('payment_amount');
  const paymentType = watch('payment_type');
  const paymentWindowDays = watch('payment_window_days');
  // Validate payment amount against max amount
  // useEffect(() => {
  //   if (maxAmount !== undefined && paymentAmount) {
  //     const amount = parseFloat(paymentAmount);
  //     if (!isNaN(amount) && amount > maxAmount) {
  //       const limitType = invoiceCategory === 'expense' ? 'Remaining BMEV' : 'Remaining BTRT';
  //       const formattedLimit = `€${maxAmount.toLocaleString()}`;

  //       setError('payment_amount', {
  //         type: 'manual',
  //         message: `Invoice Amount cannot exceed ${limitType} (${formattedLimit})`
  //       });
  //     } else {
  //       // Clear error if amount is within limit
  //       if (errors.payment_amount?.type === 'manual') {
  //         clearErrors('payment_amount');
  //       }
  //     }
  //   } else if (maxAmount !== undefined && !paymentAmount) {
  //     // Clear error if amount is cleared
  //     if (errors.payment_amount?.type === 'manual') {
  //       clearErrors('payment_amount');
  //     }
  //   }
  // }, [paymentAmount, maxAmount, invoiceCategory]);

  // Clear errors when field values change (but not for payment_amount as it's handled above).
  // Using watch's subscription API — this does NOT trigger component re-renders.
  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name && name !== 'payment_amount' && (errors as any)[name] && values[name] && values[name] !== '') {
        clearErrors(name as any);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, errors, clearErrors]);

  useEffect(() => {
    if (!paymentIssueDate) {
      if (paymentDate) {
        setValue('payment_date', null);
      }
      if (paymentTermDate) {
        setValue('payment_term_date', null);
      }
      return;
    }

    if (paymentDate && paymentDate < paymentIssueDate) {
      setValue('payment_date', paymentIssueDate);
    }
  }, [paymentIssueDate, paymentDate, paymentTermDate, setValue]);

  // useEffect(() => {
  //   // if (!paymentDate && paymentType === 'ALREADY_PAID') {
  //   //   if (paymentTermDate) {
  //   //     setValue('payment_term_date', null);
  //   //   }
  //   //   return;
  //   // }

  //   // if (paymentTermDate && paymentTermDate < paymentDate) {
  //   //   setValue('payment_term_date', paymentDate);
  //   // }
  //   if (paymentTermDate && paymentTermDate < paymentIssueDate) {
  //     setValue('payment_term_date', paymentDate);
  //   }
  // }, [paymentDate, paymentTermDate, setValue]);

  // When payment term type is "window", set payment_term_date = issue date + payment_window_days
  useEffect(() => {
    if (paymentTermType !== 'window') return;
    if (!paymentIssueDate || paymentWindowDays == null || paymentWindowDays === '') return;
    const days = Number(paymentWindowDays);
    if (!Number.isInteger(days) || days < 1) return;
    const date = new Date(paymentIssueDate);
    date.setDate(date.getDate() + days);
    setValue('payment_term_date', date);
  }, [paymentTermType, paymentIssueDate, paymentWindowDays, setValue]);

  // When payment term type is "fixed", set payment_window_days = days between issue date and payment_term_date
  useEffect(() => {
    if (paymentTermType !== 'fixed') return;
    if (!paymentIssueDate || !paymentTermDate) return;
    const issue = new Date(paymentIssueDate);
    const term = new Date(paymentTermDate);
    const diffMs = term.getTime() - issue.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    setValue('payment_window_days', Math.max(1, diffDays));
  }, [paymentTermType, paymentIssueDate, paymentTermDate, setValue]);

  return (
    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Display existing images from draft */}
      {existingImageUrls.length > 0 && (
        <div className="md:col-span-2 space-y-2">
          <label className="text-sm font-medium">Existing Invoice Images</label>
          <div className="flex flex-wrap gap-2">
            {existingImageUrls.map((url, index) => (
              <div key={url} className="relative group">
                <img
                  src={url}
                  alt={`Invoice ${index + 1}`}
                  className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => onRemoveExistingImage?.(url)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove image"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <UploadFilesField
        label={existingImageUrls.length > 0 ? 'Add More Images' : 'Upload Invoice'}
        supportedFormats={['JPEG', 'PNG', 'PDF']}
        max={6 - existingImageUrls.length}
        required={existingImageUrls.length === 0}
        value={uploadedFiles}
        onChange={onFilesChange}
        className="md:col-span-2"
      />

      <InputField
        label="Invoice Name"
        placeholder="Enter Invoice Name"
        className="md:col-span-1"
        name="name"
        type="text"
        register={register}
        error={errors.name}
        size="sm"
      />

      <SelectField
        label="Select a Budget this invoice belongs to"
        placeholder="Select Budget"
        className="md:col-span-1"
        name="budget_id"
        value={watch('budget_id')}
        options={budgetOptions}
        register={register}
        error={errors.budget_id}
        size="sm"
      />

      <InputField
        label="Invoicing company / person name"
        placeholder="Enter company / person name..."
        className="md:col-span-1"
        name="invoicing_company_name"
        type="text"
        register={register}
        error={errors.invoicing_company_name}
        size="sm"
      />

      <InputField
        label="Invoiced company / person name"
        placeholder="Enter company / person name..."
        className="md:col-span-1"
        name="invoiced_company_name"
        type="text"
        register={register}
        error={errors.invoiced_company_name}
        size="sm"
      />

      <TabRadioField
        label="Invoice Type"
        name="invoice_category"
        options={[
          { value: 'expense', label: 'Expense' },
          { value: 'revenue', label: 'Revenue' }
        ]}
        register={register}
        error={errors.invoice_category}
        size="sm"
        className="md:col-span-1"
        defaultValue="expense"
        value={watch('invoice_category')}
      />

      <TabRadioField
        label="Payment information"
        name="payment_type"
        options={[
          { value: 'ALREADY_PAID', label: 'Already Paid' },
          { value: 'NOT_PAID', label: 'Not Paid' }
        ]}
        register={register}
        error={errors.payment_type}
        size="sm"
        className="md:col-span-1"
        defaultValue="ALREADY_PAID"
        value={watch('payment_type')}
      />

      <SelectField
        label="Currency"
        placeholder="Select Currency"
        className="md:col-span-1"
        value={watch('payment_currency')}
        name="payment_currency"
        options={currencies}
        register={register}
        error={errors.payment_currency}
        size="sm"
      />

      <InputField
        label="Amount"
        placeholder={isAmountDisabled ? 'Select budget to enable' : 'e.g 100000'}
        name="payment_amount"
        type="number"
        icon={euroIcon}
        iconAlt="Euro"
        register={register}
        error={errors.payment_amount}
        size="sm"
        min={1}
        disabled={isAmountDisabled}
      />

      <DatePickerField
        label="Issue date"
        register={register}
        name="payment_issue_date"
        value={paymentIssueDate}
        onChange={(date) => setValue('payment_issue_date', date)}
        className="flex-1"
        error={errors.payment_issue_date}
        size="sm"
        tooltipText="-"
      />

      <div>
        <label className={`text-[#495057] mb-[0.45rem] font-medium text-xs md:text-sm block`}>Payment term</label>
        <RadioGroup value={paymentTermType} onValueChange={setPaymentTermType} className="w-fit flex gap-8 mb-1.5">
          <div className="flex items-center gap-2">
            <RadioGroupItem value="fixed" id="r2" />
            <label htmlFor="r2" className="cursor-pointer text-xs font-medium">
              Fixed Date
            </label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="window" id="r3" />
            <label htmlFor="r3" className="cursor-pointer text-xs font-medium">
              Payment Window
            </label>
          </div>
        </RadioGroup>
        {paymentTermType === 'fixed' ? (
          <DatePickerField
            label=""
            register={register}
            name="payment_term_date"
            value={paymentTermDate}
            onChange={(date) => setValue('payment_term_date', date)}
            disabled={!paymentIssueDate}
            minDate={paymentIssueDate ?? undefined}
            className="flex-1"
            error={errors.payment_term_date}
            size="sm"
            placeholder={!paymentIssueDate ? 'Select issue date to enable' : 'Select date'}
          />
        ) : (
          <div className="relative ">
            <InputField
              label=""
              placeholder=""
              name="payment_window_days"
              type="number"
              register={register}
              error={errors.payment_term_date}
              size="sm"
              disabled={!paymentIssueDate}
              min={1}
              inputClassName="h-[42px]"
            />
            <span className="absolute top-[12.5px] right-3.5 text-xs text-secondary font-medium">
              Days from Invoice
            </span>
          </div>
        )}
      </div>

      <InputField
        label="Invoicing Contact"
        placeholder="Enter email or phone number..."
        name="invoicing_contact"
        type="text"
        register={register}
        error={errors.invoicing_contact}
        size="sm"
      />

      <DatePickerField
        label="Payment date"
        register={register}
        name="payment_date"
        value={paymentDate}
        onChange={(date) => setValue('payment_date', date)}
        disabled={!paymentIssueDate || paymentType === 'NOT_PAID'}
        minDate={paymentIssueDate ?? undefined}
        maxDate={paymentType === 'ALREADY_PAID' ? new Date() : undefined}
        className="flex-1"
        error={errors.payment_date}
        size="sm"
        tooltipText="-"
        placeholder={
          paymentType === 'NOT_PAID'
            ? 'Filled by validator'
            : !paymentIssueDate
              ? 'Select issue date to enable'
              : 'Select date'
        }
      />
    </div>
  );
};

export default CreateInvoiceUpload;
